import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { QueryPagination, createQueryOptions } from '../../../query';
import { useStoreApi } from '../../api';
import { storeKeys } from '../../query-key-factory';
import { StoreItemsResponse } from '../../types';
import { CampaignStoreItems } from '../../types/campaign';
import { CampaignStoreItemTypes } from '../../types/utils';
import { CampaignSearch, GetCampaignsMeta } from '../types';
import { createCauseQueryString } from '../utils/createQueryString';
import { storeItemWithCrowdfundingType } from '../utils/storeItemTypes';

const useGetFundraisers = () => {
  const StoreApi = useStoreApi();

  const getFundraisers = async (context: {
    meta?: GetCampaignsMeta;
  }): Promise<StoreItemsResponse<CampaignStoreItemTypes[]>> => {
    const { queryString } = context.meta || {};
    const { data } = await StoreApi.get<StoreItemsResponse<CampaignStoreItemTypes[]>>(
      `/store_items/fundraisers?${queryString}`
    );
    return { ...data, store_items: data.store_items.map(storeItemWithCrowdfundingType) };
  };
  return getFundraisers;
};

const useListCampaignFundraisers = <T = StoreItemsResponse<CampaignStoreItems>>({
  pagination,
  queryOptions,
  select,
  causeId,
  _enabled = true,
}: {
  pagination?: QueryPagination;
  queryOptions?: CampaignSearch;
  select?: (data: StoreItemsResponse<CampaignStoreItems>) => T;
  causeId?: string;
  _enabled?: boolean;
} = {}) => {
  const causeQuery = createCauseQueryString(causeId);
  const getFundraisers = useGetFundraisers();
  const { queryString, queryObject, queryPagination } = createQueryOptions<CampaignSearch>({
    search: queryOptions,
    pagination,
    existingSearchString: causeQuery,
  });

  const queryKey = causeId ? storeKeys.id(causeId).items.fundraisers.all : [];

  return useQuery({
    queryKey: [...queryKey, queryObject, queryPagination],
    queryFn: getFundraisers,
    enabled: !!causeId && _enabled,
    meta: { queryString, causeId },
    select,
    placeholderData: keepPreviousData,
  });
};

export { useListCampaignFundraisers };
